<template>
  <div class="review-grid">
    <div>
      <div v-if="missing.length" class="missing-card">
        <div class="missing-card__head">
          <LtIcon name="alert" :size="18" />
          <span>חסרים פרטים חובה</span>
        </div>
        <p class="muted" style="margin: 6px 0 8px; font-size: 13px">
          סיימו את הפריטים הבאים כדי שיהיה אפשר לשלוח לאישור.
        </p>
        <ul class="missing-list">
          <li v-for="m in missing" :key="m.key">
            <span>{{ m.label }}</span>
            <button type="button" class="btn btn--link btn--sm" @click="$emit('jump', m.step)">
              לשלב <LtIcon name="arrow-left" :size="14" />
            </button>
          </li>
        </ul>
      </div>

      <div class="review-block">
        <div class="review-block__head">
          <h4 class="review-block__title">פרטים כלליים</h4>
          <button type="button" class="btn btn--link btn--sm" @click="$emit('jump', 0)">עריכה</button>
        </div>
        <dl class="review-list">
          <dt>שם הלינקטון</dt>
          <dd>{{ form.name || '—' }}</dd>
          <dt>סוג</dt>
          <dd>{{ typeMeta.name }}</dd>
          <dt>אומן</dt>
          <dd>{{ artistName }}</dd>
          <template v-if="form.label && form.label.length">
            <dt>לייבל</dt>
            <dd>{{ form.label.map((c) => c.name).join(', ') }}</dd>
          </template>
          <template v-if="form.releaseDate">
            <dt>תאריך יציאה</dt>
            <dd>{{ form.releaseDate }}</dd>
          </template>
        </dl>
      </div>

      <div class="review-block">
        <div class="review-block__head">
          <h4 class="review-block__title">תמונות</h4>
          <button type="button" class="btn btn--link btn--sm" @click="$emit('jump', 1)">עריכה</button>
        </div>
        <dl class="review-list">
          <dt>תמונה ראשית</dt>
          <dd>{{ form.images.main ? '✓ הועלתה' : '— חסרה' }}</dd>
          <dt>תמונת קטלוג</dt>
          <dd>{{ form.images.catalog ? '✓ הועלתה' : 'אופציונלי' }}</dd>
          <dt>תמונה למייל</dt>
          <dd>{{ form.images.email ? '✓ הועלתה' : 'אופציונלי' }}</dd>
          <dt>גלריה</dt>
          <dd>{{ form.images.gallery.length }} תמונות</dd>
        </dl>
      </div>

      <div class="review-block">
        <div class="review-block__head">
          <h4 class="review-block__title">וידאו</h4>
          <button type="button" class="btn btn--link btn--sm" @click="$emit('jump', 2)">עריכה</button>
        </div>
        <dl class="review-list">
          <dt>YouTube</dt>
          <dd>{{ form.youtube || 'ללא קליפ' }}</dd>
        </dl>
      </div>

      <div class="review-block">
        <div class="review-block__head">
          <h4 class="review-block__title">תקשורת</h4>
          <button type="button" class="btn btn--link btn--sm" @click="$emit('jump', 3)">עריכה</button>
        </div>
        <dl class="review-list">
          <dt>קומוניקט PDF</dt>
          <dd>{{ form.comms.pressReleasePdf ? '✓ ' + form.comms.pressReleasePdf.name : '— חסר' }}</dd>
          <dt>קומוניקט Word</dt>
          <dd>{{ form.comms.pressReleaseDoc ? '✓ ' + form.comms.pressReleaseDoc.name : '— חסר' }}</dd>
          <dt>מילים</dt>
          <dd>{{ form.comms.lyrics ? '✓ הועלה' : 'אופציונלי' }}</dd>
          <dt>קרדיטים</dt>
          <dd>{{ form.comms.credits ? '✓ הועלה' : 'אופציונלי' }}</dd>
        </dl>
      </div>

      <div class="review-block">
        <div class="review-block__head">
          <h4 class="review-block__title">רצועות</h4>
          <button type="button" class="btn btn--link btn--sm" @click="$emit('jump', 4)">עריכה</button>
        </div>
        <p v-if="!form.tracks.length" class="muted" style="margin: 0; font-size: 13.5px">עדיין לא הוספת רצועות.</p>
        <ol v-else style="margin: 0; padding-inline-start: 18px; display: flex; flex-direction: column; gap: 6px">
          <li v-for="(t, i) in form.tracks" :key="i" style="font-size: 14px">
            <strong>{{ t.name || 'ללא שם' }}</strong>
            <span v-if="t.duration" class="muted"> · {{ t.duration }}</span>
          </li>
        </ol>
      </div>
    </div>

    <aside class="review-summary">
      <h4>סוג לינקטון</h4>
      <div style="font-weight: 700; font-size: 18px">{{ typeMeta.name }}</div>
      <div class="muted" style="font-size: 13px; margin-top: 2px">עד {{ typeMeta.trackLimit }} רצועות</div>

      <div style="margin-top: 14px">
        <h4>סכום לתשלום</h4>
        <div class="price">
          <template v-if="payMode === 'balance'">₪0</template>
          <template v-else>₪{{ formatPrice(typeMeta.price) }}</template>
        </div>
      </div>

      <div class="pay-mode" :class="{ 'is-balance': payMode === 'balance' }">
        <template v-if="payMode === 'balance'">
          <strong>זמין ביתרה</strong> · ניצול לינקטון אחד מהמארז שרכשתם.
          <div class="muted" style="font-size: 12px; margin-top: 4px">לא נדרש תשלום נוסף.</div>
        </template>
        <template v-else>
          <strong>תשלום חד-פעמי</strong> · התשלום מבוצע רק כעת — בעת שליחה לאישור.
        </template>
      </div>

      <div class="muted" style="font-size: 12.5px; line-height: 1.6">
        ✓ אפשר להמשיך לערוך גם אחרי שליחה<br />
        ✓ קבלת אישור על שליחה במייל<br />
        ✓ אנחנו נעדכן אם יידרשו תיקונים
      </div>
    </aside>
  </div>
</template>

<script>
import LtIcon from '../LtIcon.vue';
import { wizardFormInject } from '../wizardInject.js';

export default {
  name: 'StepReview',
  mixins: [wizardFormInject],
  components: { LtIcon },
  props: {
    typeMeta: { type: Object, required: true },
    artistName: { type: String, default: '' },
    balance: { type: Number, default: 0 },
    missing: { type: Array, default: () => [] },
  },
  emits: ['jump'],
  computed: {
    payMode() {
      return this.balance > 0 ? 'balance' : 'pay';
    },
  },
  methods: {
    formatPrice(price) {
      return price.toLocaleString('he-IL');
    },
  },
};
</script>
